import React from 'react';
import { CssBaseline } from '@mui/material';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { ThemeModeProvider } from './context/ThemeContext';
import ProtectedRoute from './routes/ProtectedRoute';
import { PAYMENTS_ROLE_ALIASES } from './constants/roles';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NewsList from './pages/cms/NewsList';
import NewsDetail from './pages/cms/NewsDetail';
import NewsletterList from './pages/cms/NewsletterList';
import PresidentsList from './pages/cms/PresidentsList';
import PresidentDetail from './pages/cms/PresidentDetail';
import BoardsList from './pages/cms/BoardsList';
import BoardDetail from './pages/cms/BoardDetail';
import ClubEventsList from './pages/cms/ClubEventsList';
import ClubEventDetail from './pages/cms/ClubEventDetail';
import LiveSurveyControl from './pages/cms/LiveSurveyControl';
import Galleries from './pages/cms/Galleries';
import GalleriesDetail from './pages/cms/GalleriesDetail';
import WeeklyMeetings from './pages/events/WeeklyMeetings';
import UsersList from './pages/users/UsersList';
import GroupsList from './pages/groups/GroupsList';
import MembersList from './pages/groups/MembersList';
import MemberDetail from './pages/groups/MemberDetail';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Announcements from './pages/Announcements';
import AnnouncementDetail from './pages/AnnouncementDetail';
import Emails from './pages/notifications/Emails';
import AppNotifications from './pages/notifications/AppNotifications';
import PushNotificationRecordsTab from './pages/notifications/components/PushNotificationRecordsTab';
import Sms from './pages/notifications/Sms';
import SurveysList from './pages/SurveysList';
import SurveyDetail from './pages/SurveyDetail';
import SurveyResponses from './pages/SurveyResponses';
import SurveyParticipantResponse from './pages/SurveyParticipantResponse';
import Dues from './pages/payments/Dues';
import DuesDetail from './pages/payments/DuesDetail';
import WeeklyMeetingMeals from './pages/payments/WeeklyMeetingMeals';
import WeeklyMeetingMealDetail from './pages/payments/WeeklyMeetingMealDetail';
import EventDues from './pages/payments/EventDues';
import EventPaymentDetail from './pages/payments/EventPaymentDetail';
import DonationCards from './pages/payments/DonationCards';
import ProjectPayments from './pages/payments/ProjectPayments';
import MiscPayments from './pages/payments/MiscPayments';
import SkippedAutomaticMealDebts from './pages/payments/SkippedAutomaticMealDebts';
import ProductDetail from './pages/payments/ProductDetail';
import MeetingDetail from './pages/events/MeetingDetail';
import ParticipationCancellationRequests from './pages/events/ParticipationCancellationRequests';

function App() {
  return (
    <ThemeModeProvider>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route element={<ProtectedRoute />}> {/* authenticated only */}
              {/* Live Survey Control - No Layout (Full Screen) */}
              <Route path="live-survey/:surveyId" element={<LiveSurveyControl />} />
              
              <Route element={<MainLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="news" element={<NewsList />} />
                <Route path="news/:id" element={<NewsDetail />} />
                <Route path="newsletters" element={<NewsletterList />} />
                <Route path="presidents" element={<PresidentsList />} />
                <Route path="presidents/:id" element={<PresidentDetail />} />
                <Route path="boards" element={<BoardsList />} />
                <Route path="boards/:id" element={<BoardDetail />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route path="events" element={<ClubEventsList />} />
                <Route path="events/cancellation-requests" element={<ParticipationCancellationRequests />} />
                <Route path="events/weekly-meetings" element={<WeeklyMeetings />} />
                <Route path="events/weekly-meetings/:id" element={<MeetingDetail />} />
                <Route path="events/:id" element={<ClubEventDetail />} />
                <Route path="galleries" element={<Galleries />} />
                <Route path="galleries/:id" element={<GalleriesDetail />} />
                <Route path="users" element={<UsersList />} />
                <Route path="groups" element={<GroupsList />} />
                <Route path="members" element={<MembersList />} />
                <Route path="members/:id" element={<MemberDetail />} />
                <Route path="announcements" element={<Announcements />} />
                <Route path="announcements/:id" element={<AnnouncementDetail />} />
                <Route path="notifications/emails" element={<Emails />} />
                <Route path="notifications/app-notifications" element={<AppNotifications />} />
                <Route path="notifications/app-notifications/records" element={<PushNotificationRecordsTab />} />
                <Route path="notifications/sms" element={<Sms />} />
                <Route path="surveys" element={<SurveysList />} />
                <Route path="surveys/:id" element={<SurveyDetail />} />
                <Route path="surveys/:id/responses" element={<SurveyResponses />} />
                <Route path="surveys/:id/participant-response" element={<SurveyParticipantResponse />} />
                <Route element={<ProtectedRoute allowedRoles={PAYMENTS_ROLE_ALIASES} />}>
                  <Route path="payments/dues" element={<Dues />} />
                  <Route path="payments/dues/:id" element={<DuesDetail />} />
                  <Route path="payments/weekly-meetings" element={<WeeklyMeetingMeals />} />
                  <Route path="payments/weekly-meetings/:id" element={<WeeklyMeetingMealDetail />} />
                  <Route path="payments/event-dues" element={<EventDues />} />
                  <Route path="payments/event-dues/:eventId" element={<EventPaymentDetail />} />
                  <Route path="payments/donation-cards" element={<DonationCards />} />
                  <Route path="payments/project-payments" element={<ProjectPayments />} />
                  <Route path="payments/misc" element={<MiscPayments />} />
                  <Route path="payments/misc/:id" element={<ProductDetail />} />
                  <Route path="payments/skipped-meal-debts" element={<SkippedAutomaticMealDebts />} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Login />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ThemeModeProvider>
  );
}

export default App;
